/**
 * /operations/attendance/exception-queue — Session 3B PR 3.
 *
 * Owning agent: Mutaba'aa (anomaly detection · compliance flags).
 * AI surfaces: AiBriefPanel + InlineAISuggestion (device-outage flag)
 * + TraceableValue (severity derivation).
 */

import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, CheckCircle2, Play, ShieldAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { AttendanceExceptionItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StatusFilter = "open" | "resolved" | "false_positive" | "all";

function _severityToneClass(s: AttendanceExceptionItem["severity"]): string {
  if (s === "critical") return "bg-rose-100 text-rose-800";
  if (s === "warning") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

export function AttendanceExceptionQueuePage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");

  const list = useQuery({
    queryKey: ["session3b-attendance-exceptions", legalEntityId],
    queryFn: () => session3bApi.listAttendanceExceptions(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: AttendanceExceptionItem[] = list.data?.items ?? [];
  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((r) => r.status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(() => {
    const open = items.filter((r) => r.status === "open").length;
    const critical = items.filter((r) => r.severity === "critical").length;
    return {
      total: items.length,
      attention: open,
      critical,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("attendance.exception_queue.title", "Attendance Exception Queue")}
          subtitle={t(
            "attendance.exception_queue.subtitle",
            "Missed punches · biometric anomalies · clock-in mismatches needing HR action. Mutaba'aa flags critical patterns.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["open", "resolved", "false_positive", "all"] as StatusFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  statusFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`attendance.exception_queue.filter.${f}`, f)}
                {f === "open" && counts.attention > 0 && (
                  <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-600 px-1 text-[9px] font-semibold text-white">
                    {counts.attention}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "attendance.exception_queue.cta.scan_note",
                  "Manually triggers Mutaba'aa's attendance anomaly scanner. Live trigger wires to the scheduler in a follow-up; today the queue auto-fills as the scanner ticks.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Play className="h-4 w-4" />
            {t("attendance.exception_queue.cta.scan", "Run Anomaly Scan Now")}
          </button>
        </div>

        {counts.critical > 0 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<AlertOctagon className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "attendance.exception_queue.suggestion.critical_headline",
              `${counts.critical} critical exception${counts.critical === 1 ? "" : "s"} flagged — investigate the biometric sync log.`,
            )}
            body={t(
              "attendance.exception_queue.suggestion.critical_body",
              "Mutaba'aa pattern signal: clustered biometric failures usually point to one device offline. Open the Biometric Sync Log to diagnose.",
            )}
            primaryAction={{
              label: t(
                "attendance.exception_queue.suggestion.open_sync_log",
                "Open Biometric Sync",
              ),
              onClick: () =>
                window.location.assign("/operations/attendance/biometric-sync"),
            }}
            suggestionKey="exception-critical"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "attendance.exception_queue.suggestion.empty_headline",
              "No attendance exceptions detected.",
            )}
            body={t(
              "attendance.exception_queue.suggestion.empty_body",
              "Mutaba'aa notes: attendance signals are clean for this window. The scanner runs every 15 min — new anomalies surface automatically.",
            )}
            primaryAction={{
              label: t(
                "attendance.exception_queue.suggestion.open_grid",
                "View attendance grid",
              ),
              onClick: () => window.location.assign("/operations/attendance"),
            }}
            suggestionKey="exception-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("attendance.exception_queue.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("attendance.exception_queue.col.date", "Date")}</th>
                  <th className="px-3 py-2">{t("attendance.exception_queue.col.type", "Type")}</th>
                  <th className="px-3 py-2">{t("attendance.exception_queue.col.severity", "Severity")}</th>
                  <th className="px-3 py-2">{t("attendance.exception_queue.col.reason", "Reason")}</th>
                  <th className="px-3 py-2">{t("attendance.exception_queue.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.exception_id}
                    className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span dir="ltr">{row.date}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">{row.exception_type}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "attendance.exception_queue.trace.severity",
                              `Severity = derived from exception_type. biometric_failure → critical (device-level); manual_override → warning (anomaly_detected); missing-punches → info. Mutaba'aa scanner classifies on detection.`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_severityToneClass(row.severity)}`}
                        >
                          {row.severity}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-tertiary">{row.reason}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "attendance.exception_queue.action.resolve_note",
                                "Resolve — prompt for explanation, then write workflow_event with from_state='open' to_state='resolved'.",
                              ),
                            )
                          }
                          className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                        >
                          <CheckCircle2 className="me-1 inline h-3 w-3" />
                          {t("attendance.exception_queue.action.resolve", "Resolve")}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "attendance.exception_queue.action.escalate_note",
                                "Escalate to HR Director — routes to the inbox queue.",
                              ),
                            )
                          }
                          className="rounded border border-amber-300 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-50"
                        >
                          <ShieldAlert className="me-1 inline h-3 w-3" />
                          {t("attendance.exception_queue.action.escalate", "Escalate")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("attendance.exception_queue.title", "Exception Queue"),
            window_label: t("attendance.exception_queue.window_label", "past 7 days"),
          }}
        />
      </aside>
    </div>
  );
}
